// Low-level Web Audio synthesis helpers shared by the procedural music sources.
// Everything here is stateless and one-shot: a voice schedules its own oscillators
// / envelopes at an absolute AudioContext time and cleans itself up when it ends.
// This keeps the generative sequencers (music-sources.ts) free of raw node wiring.

// MIDI note number -> frequency in Hz (A4 / MIDI 69 = 440Hz).
export function mtof(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// Note-name helpers so patterns read musically. e.g. note("A", 3) === MIDI 57.
const SEMITONES: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5,
  "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};
export function note(name: string, octave: number): number {
  return (octave + 1) * 12 + SEMITONES[name];
}

// A reusable white-noise buffer (1s, mono). Created lazily per context.
const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();
export function getNoiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseBuffers.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseBuffers.set(ctx, buf);
  }
  return buf;
}

interface PluckOpts {
  freq: number;
  time: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  cutoff?: number; // optional lowpass for a softer tone
  detune?: number;
}

// A short tonal note with a percussive amp envelope (synth pluck / bell / stab).
export function pluck(ctx: AudioContext, dest: AudioNode, o: PluckOpts): void {
  const { freq, time, duration } = o;
  const type = o.type ?? "triangle";
  const peak = o.gain ?? 0.2;
  const attack = o.attack ?? 0.005;

  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, time);
  if (o.detune) osc.detune.setValueAtTime(o.detune, time);

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, time);
  amp.gain.exponentialRampToValueAtTime(peak, time + attack);
  amp.gain.exponentialRampToValueAtTime(0.0001, time + duration);

  let tail: AudioNode = amp;
  osc.connect(amp);
  if (o.cutoff) {
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(o.cutoff, time);
    amp.connect(filter);
    tail = filter;
  }
  tail.connect(dest);

  osc.start(time);
  osc.stop(time + duration + 0.05);
}

interface PadOpts {
  freqs: number[];
  time: number;
  duration: number;
  gain?: number;
  attack?: number;
  release?: number;
  cutoff?: number;
  type?: OscillatorType;
}

// A sustained chord pad: detuned oscillator stack through a lowpass with a slow
// attack/release. Used for the lush menu harmony.
export function pad(ctx: AudioContext, dest: AudioNode, o: PadOpts): void {
  const { freqs, time, duration } = o;
  const peak = o.gain ?? 0.08;
  const attack = o.attack ?? 0.8;
  const release = o.release ?? 1.2;
  const type = o.type ?? "sawtooth";

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, time);
  amp.gain.exponentialRampToValueAtTime(peak, time + attack);
  amp.gain.setValueAtTime(peak, time + duration);
  amp.gain.exponentialRampToValueAtTime(0.0001, time + duration + release);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(o.cutoff ?? 1400, time);
  amp.connect(filter);
  filter.connect(dest);

  const oscs: OscillatorNode[] = [];
  freqs.forEach((f) => {
    // two slightly detuned voices per note for a wide analog feel
    [-6, 6].forEach((cents) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(f, time);
      osc.detune.setValueAtTime(cents, time);
      osc.connect(amp);
      oscs.push(osc);
    });
  });
  oscs.forEach((osc) => {
    osc.start(time);
    osc.stop(time + duration + release + 0.1);
  });
}

// Punchy synth kick: a sine with a fast downward pitch sweep + amp decay.
export function kick(ctx: AudioContext, dest: AudioNode, time: number, gain = 0.9): void {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(45, time + 0.12);

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(gain, time);
  amp.gain.exponentialRampToValueAtTime(0.0001, time + 0.28);

  osc.connect(amp);
  amp.connect(dest);
  osc.start(time);
  osc.stop(time + 0.3);
}

// Hi-hat: filtered noise burst. `open` lengthens the decay.
export function hat(ctx: AudioContext, dest: AudioNode, time: number, gain = 0.15, open = false): void {
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);

  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.setValueAtTime(7000, time);

  const amp = ctx.createGain();
  const decay = open ? 0.18 : 0.045;
  amp.gain.setValueAtTime(gain, time);
  amp.gain.exponentialRampToValueAtTime(0.0001, time + decay);

  src.connect(hp);
  hp.connect(amp);
  amp.connect(dest);
  src.start(time);
  src.stop(time + decay + 0.05);
}

// Clap / snare-ish layer: band-passed noise with a couple of quick transients.
export function clap(ctx: AudioContext, dest: AudioNode, time: number, gain = 0.3): void {
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(1800, time);
  bp.Q.setValueAtTime(0.7, time);

  const amp = ctx.createGain();
  amp.connect(dest);
  bp.connect(amp);

  // three closely spaced noise bursts read as a clap
  [0, 0.012, 0.024].forEach((offset, i) => {
    const src = ctx.createBufferSource();
    src.buffer = getNoiseBuffer(ctx);
    const g = ctx.createGain();
    const t = time + offset;
    const peak = gain * (i === 2 ? 1 : 0.6);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, t + (i === 2 ? 0.16 : 0.05));
    src.connect(g);
    g.connect(bp);
    src.start(t);
    src.stop(t + 0.2);
  });
}
